import PCSpool from '../Spools/PCSpool';
import pairKey from '../Spools/pairKey';

export interface RouteVolumePipeBreakdown {
  pipeId: string;
  pipeName: string;
  pipeColour: string;
  insideDiameter: number; // m
  lengthMetres: number;
  volumeCubicMetres: number;
}

export interface RouteVolume {
  totalLengthMetres: number;
  totalVolumeCubicMetres: number;
  unsizedLengthMetres: number; // length of spools with no pipe / no inside diameter
  breakdown: RouteVolumePipeBreakdown[];
}

const UNKNOWN_PIPE_ID = 'unknown';
const UNKNOWN_PIPE_NAME = 'Unknown';
const UNKNOWN_PIPE_COLOUR = '#888888';

const buildSpoolEdgeIndex = (spools: Record<string, PCSpool>): Map<string, PCSpool> => {
  const index = new Map<string, PCSpool>();
  Object.values(spools).forEach((spool) => {
    const a = spool.pointA?.id;
    const b = spool.pointB?.id;
    if (a && b) index.set(pairKey(a, b), spool);
  });
  return index;
};

export const crossSectionalArea = (insideDiameter: number): number => (insideDiameter > 0
  ? (Math.PI * insideDiameter ** 2) / 4
  : 0);

// Internal volume of a single spool, using its resolved pipe's inside diameter (m) and its
// full length (m), choke length included. Spools without a sized pipe contribute no volume.
export const spoolVolume = (spool: PCSpool): number => crossSectionalArea(spool.pipe?.insideDiameter ?? 0) * spool.length();

// Maps an ordered node path onto the spools that connect it. Spools are undirected here,
// matching how the measure tool builds its search graph.
export const resolveRouteSpools = (path: string[], spools: Record<string, PCSpool>): PCSpool[] => {
  const index = buildSpoolEdgeIndex(spools);
  return path.slice(0, -1)
    .map((nodeId, i) => index.get(pairKey(nodeId, path[i + 1])))
    .filter((spool): spool is PCSpool => !!spool);
};

// Totals the internal volume of a run of spools, grouped by pipe type so a route crossing
// several pipe sizes reports each size separately.
export const computeRouteVolume = (spools: PCSpool[]): RouteVolume => {
  const grouped = spools.reduce<Record<string, RouteVolumePipeBreakdown>>((acc, spool) => {
    const { pipe } = spool;
    const pipeId = pipe?.pipeId ?? UNKNOWN_PIPE_ID;
    const insideDiameter = pipe?.insideDiameter ?? 0;
    const lengthMetres = (acc[pipeId]?.lengthMetres ?? 0) + spool.length();
    return {
      ...acc,
      [pipeId]: {
        pipeId,
        pipeName: pipe?.name ?? UNKNOWN_PIPE_NAME,
        pipeColour: pipe?.colour ?? UNKNOWN_PIPE_COLOUR,
        insideDiameter,
        lengthMetres,
        volumeCubicMetres: crossSectionalArea(insideDiameter) * lengthMetres,
      },
    };
  }, {});

  const breakdown = Object.values(grouped).sort((a, b) => b.lengthMetres - a.lengthMetres);

  return {
    totalLengthMetres: breakdown.reduce((sum, { lengthMetres }) => sum + lengthMetres, 0),
    totalVolumeCubicMetres: breakdown.reduce((sum, { volumeCubicMetres }) => sum + volumeCubicMetres, 0),
    unsizedLengthMetres: breakdown
      .filter(({ insideDiameter }) => insideDiameter <= 0)
      .reduce((sum, { lengthMetres }) => sum + lengthMetres, 0),
    breakdown,
  };
};
